/**
 * Onboarding's pure pieces (slice 1b spec §6): who goes to `/setup`, how the
 * setup steps read, the composers' gate, the providers and the model
 * catalog's grouping, and where the connect panel is. Settings → AI's own
 * pieces are in `settings/ai-view.ts`.
 */
import { ApiError, errorHint } from "../../app/api";
import type { AiConnection, AiProvider, ConnectStage, Me, SetupStep } from "../../app/me";
import { HOME } from "../../app/home";

type Computer = NonNullable<Me["computer"]>;

type Ai = Computer["ai"];

/**
 * Whether the workspace is still onboarding (slice 1b spec §6.1): the
 * computer is setting up or waiting, or the AI has never been connected
 * (`none`, or a first connect that's running or failed). A computer that's
 * been released isn't onboarding: there's nothing to set up.
 */
export function isOnboarding(computer: Me["computer"]): boolean {
  if (!computer || computer.status === "released") return false;
  if (computer.status === "setting_up" || computer.status === "waiting") return true;
  const { status, connected_at } = computer.ai;
  // A connected account that doesn't serve the default (a new computer's
  // other provider): past onboarding, to choose a default in Settings.
  if (status === "none") return !defaultUnserved(computer.ai);
  return (status === "connecting" || status === "failed") && !connected_at;
}

/**
 * OpenClaw's provider (§3.3): a ChatGPT sign-in and an OpenAI key are both
 * `openai`, serve the same models, and are tried in one account order. A
 * Grok sign-in is `xai` (slice 1f spec §3.1).
 */
export function groupOf(provider: AiProvider): string {
  if (provider === "chatgpt") return "openai";
  // A Claude subscription's models are `claude-cli/*` (slice 1g spec §5).
  if (provider === "claude") return "claude-cli";
  return provider === "grok" ? "xai" : provider;
}

/** The OpenClaw provider a catalog id names: `openrouter/…` is `openrouter`. */
export function modelGroup(model: string): string {
  return model.split("/")[0];
}

/**
 * Whether a connect sets the default model (slice 1c spec §3.4): only the
 * first connection's, with no default yet or no account at all. Any other
 * connect keeps the default and the fallbacks, so its form asks no model.
 */
export function connectPicksDefault(ai: Pick<Ai, "model" | "connections">): boolean {
  return !ai.model || (ai.connections ?? []).length === 0;
}

/**
 * The model a connect is for: the one it sent, else the workspace's default
 * when it's one of this provider's models (a later connect keeps it).
 */
export function connectModel(
  sent: string | undefined,
  current: string | null,
  models: readonly CatalogModel[],
): string | null {
  if (sent) return sent;
  return models.some((m) => m.id === current) ? current : null;
}

/**
 * Whether there's a default model no account serves, while another account
 * works: after a new computer, another provider connected before the
 * default's was reconnected. Asks wait until the owner reconnects it or
 * chooses another default.
 */
export function defaultUnserved(ai: Pick<Ai, "status" | "model" | "connections">): boolean {
  const { model } = ai;
  if (!model || (ai.status !== "none" && ai.status !== "failed")) return false;
  const connected = (ai.connections ?? []).filter((c) => c.status === "connected");
  return connected.length > 0 && !connected.some((c) => groupOf(c.provider) === modelGroup(model));
}

/**
 * Where a person belongs, if not on a page that needs `need` (slice 1b
 * spec §6.1): signed out → /login; email not confirmed → /verify (#384);
 * unpaid → /checkout; paid but onboarding → /setup (a hard wait: no skip);
 * past onboarding → the shell.
 */
export function redirectFor(
  me:
    | (Pick<Me, "workspace" | "computer"> & {
        user?: Pick<Me["user"], "role"> & Partial<Pick<Me["user"], "email_verified">>;
      })
    | null,
  need: "active" | "pending" | "verify" | "setup",
): string | null {
  if (!me) return "/login";
  // Checkout and setup are the owner's (slice 6a §2): a member goes to Chats,
  // where asks wait on the owner ("<Owner> hasn't connected the AI yet.").
  if (me.user?.role === "member") return need === "active" ? null : HOME;
  const active = me.workspace.status === "active";
  const verify = verifyRedirect(active, me.user?.email_verified === false, need);
  if (verify !== undefined) return verify;
  if (need === "pending") return active ? HOME : null;
  if (!active) return "/checkout";
  const onboarding = isOnboarding(me.computer);
  if (need === "setup") return onboarding ? null : HOME;
  return onboarding ? "/setup" : null;
}

/**
 * The emailed code's part (#384): an unpaid owner who hasn't confirmed the
 * email goes to /verify, and /verify sends anyone else on. Only checkout
 * waits on the code: a workspace already paid for never asks. Undefined
 * when it has nothing to say.
 */
function verifyRedirect(
  active: boolean,
  unconfirmed: boolean,
  need: "active" | "pending" | "verify" | "setup",
): string | null | undefined {
  const verify = !active && unconfirmed;
  if (need === "verify") return verify ? null : active ? HOME : "/checkout";
  return verify ? "/verify" : undefined;
}

/**
 * The redirect a page that lingers still takes: none home once it's
 * been shown (it goes there itself), any other one as it is.
 */
export function lingerRedirect(to: string | null, linger: boolean, shown: boolean): string | null {
  return linger && shown && to === HOME ? null : to;
}

/**
 * The §3.1 steps in order, with their labels on `/setup`. A pi computer
 * (ADR-0021) is ready at once and writes none of them, so they read as one
 * running row, then all done.
 */
const STEPS: Array<{ step: SetupStep; label: string }> = [
  { step: "sandbox", label: "Getting a computer" },
  { step: "configuring", label: "Setting up your Chief of Staff" },
];

export type StepState = "done" | "running" | "failed" | "pending";

export type StepRow = { step: SetupStep; label: string; state: StepState };

/**
 * The checklist (slice 1b spec §6.2): steps before `computer.step` are done,
 * it is running (or failed, on `error`), the rest pending. Past setup every
 * step is done. "Waiting for a computer" shows only while it's the step.
 */
export function setupSteps(computer: Pick<Computer, "status" | "step">): StepRow[] {
  const { status } = computer;
  const step = status === "waiting" ? "waiting" : computer.step;
  const rows = STEPS.filter((s) => s.step !== "waiting" || step === "waiting");
  const settingUp = status === "setting_up" || status === "waiting";
  if (!settingUp && status !== "error") return rows.map((s) => ({ ...s, state: "done" }));
  const at = rows.findIndex((s) => s.step === step);
  return rows.map((s, i) => {
    if (at === -1) return { ...s, state: settingUp && i === 0 ? "running" : "pending" };
    if (i < at) return { ...s, state: "done" };
    if (i > at) return { ...s, state: "pending" };
    return { ...s, state: status === "error" ? "failed" : "running" };
  });
}

/** Where setup is, for `/setup`'s headline: building, in line, stopped on a step, or past it. */
export function setupPhase(computer: Me["computer"]): "building" | "waiting" | "failed" | "ready" {
  if (!computer || computer.status === "setting_up") return "building";
  if (computer.status === "waiting") return "waiting";
  // An `error` after the computer was ready has no step: setup is past it.
  return computer.status === "error" && computer.step !== null ? "failed" : "ready";
}

/** "9:02 am": a time of day in the workspace's timezone. */
export function clockTime(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: timezone,
  })
    .format(new Date(iso))
    .replace(/\s*([AP])M$/, (_, m: string) => ` ${m.toLowerCase()}m`);
}

/**
 * An error as a headline and what follows it: the first sentence, then the
 * rest. A closing "Try again." goes, since a Try again button sits below it.
 */
export function errorParts(error: string): { title: string; detail: string | null } {
  const sentences = error.match(/[^.!?]+[.!?]+(?=\s|$)|[^.!?]+$/g)?.map((s) => s.trim()) ?? [];
  const kept = sentences.filter((s, i) => i === 0 || !/^Try again\.?$/.test(s));
  const [title = error, ...rest] = kept;
  return { title, detail: rest.length > 0 ? rest.join(" ") : null };
}

/** "4:05", or "1:02:09" past an hour: the time since `setup_started_at`. */
export function elapsed(since: string, now: number): string {
  const total = Math.max(0, Math.floor((now - Date.parse(since)) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
}

/**
 * Asks go through once the AI is connected, or needs reconnecting (slice 1b
 * spec §4.5), and during an update: they queue until it ends (slice 1j §4.1).
 */
export function canAsk(computer: Me["computer"]): boolean {
  if (!computer || computer.status === "released") return false;
  const { status, updating } = computer.ai;
  return status === "connected" || status === "needs_reconnect" || updating === true;
}

/**
 * Why a composer shows a strip (slice 1j §6.1): an update runs (asks queue),
 * or asks are refused though the AI has worked before (§6.1a); null before
 * the first working AI, whose gate says "Connect your AI to start" (S1b §4.5).
 */
export function askNotice(computer: Me["computer"]): "updating" | "locked" | null {
  if (!computer || computer.status === "released") return null;
  if (computer.ai.updating) return "updating";
  if (canAsk(computer) || !computer.ai.connected_at) return null;
  return "locked";
}

/** Where "Connect your AI" goes: `/setup` while onboarding, else Settings → AI (§4.5). */
export function connectHref(computer: Me["computer"]): string {
  return isOnboarding(computer) ? "/setup" : "/settings";
}

export type CatalogModel = {
  id: string;
  label: string;
  generation: "current" | "last";
  default: boolean;
  /** Slice 2f: it takes images, so it can be the image model. */
  image?: boolean;
};

export type Catalog = Record<AiProvider, CatalogModel[]>;

export type ModelGroup = { label: string; models: CatalogModel[] };

/** The model list grouped "Current generation" / "Previous generation" (§4.4), empty groups left out. */
export function modelGroups(models: readonly CatalogModel[]): ModelGroup[] {
  const groups: ModelGroup[] = [
    { label: "Current generation", models: models.filter((m) => m.generation === "current") },
    { label: "Previous generation", models: models.filter((m) => m.generation === "last") },
  ];
  return groups.filter((g) => g.models.length > 0);
}

/** The provider's default, or its first model. */
export function defaultModelOf(models: readonly CatalogModel[]): string | undefined {
  return (models.find((m) => m.default) ?? models[0])?.id;
}

/** A model's label from the catalog, or its id when the catalog doesn't have it. */
export function modelLabel(catalog: Catalog | undefined, model: string | null): string | null {
  if (!model) return null;
  for (const models of Object.values(catalog ?? {})) {
    const found = models.find((m) => m.id === model);
    if (found) return found.label;
  }
  return model;
}

/**
 * The launch providers (slice 1b spec §1, slice 1f spec §7): the name, how
 * each one connects ("Sign in" or "API key", slice 1c spec §6.1), and what
 * it is. Their order is the Add a provider grid's.
 */
export const PROVIDERS: Record<AiProvider, { name: string; connects: string; about: string }> = {
  chatgpt: { name: "ChatGPT", connects: "Sign in", about: "Sign in with your account" },
  openai: { name: "OpenAI", connects: "API key", about: "GPT, with an API key" },
  openrouter: { name: "OpenRouter", connects: "API key", about: "Open models, with an API key" },
  grok: { name: "Grok", connects: "Sign in", about: "Sign in with SuperGrok" },
  anthropic: { name: "Anthropic", connects: "API key", about: "Claude, with an API key" },
  claude: {
    name: "Claude",
    connects: "Setup token",
    about: "Your Claude plan, with a setup token",
  },
  "workers-ai": {
    name: "Workers AI",
    connects: "API token",
    about: "Open models on Cloudflare",
  },
  // Slice 1o: Runware runs on pi, every computer's engine.
  runware: { name: "Runware", connects: "API key", about: "Open models, with a Runware API key" },
};

/** The workspace's Workers AI account's Cloudflare account ID, if it has one (slice 1h spec §8). */
export function workersAiAccountId(ai: Ai): string | undefined {
  return ai.connections?.find((c) => c.provider === "workers-ai")?.account_id;
}

/** The providers that sign in with a device code rather than take a key. */
export type SignInProvider = "chatgpt" | "grok";

export function signsIn(provider: AiProvider | null | undefined): provider is SignInProvider {
  return provider === "chatgpt" || provider === "grok";
}

/**
 * Where a sign-in's code is typed, in the owner's words (slice 1b spec §6.2,
 * slice 1f spec §7): the site, and what to know before approving. Grok's
 * code lives as long as xAI says, which the wait below it counts down.
 */
export const SIGN_IN_SITES: Record<SignInProvider, { site: string; note: string }> = {
  chatgpt: {
    site: "ChatGPT",
    note: "Open ChatGPT, type the code, then approve Crew. It works once, for the next 15 minutes.",
  },
  grok: {
    site: "xAI",
    note: "Open xAI, type the code, then approve. xAI may call the app “Grok Build” on its consent screen.",
  },
};

/**
 * A refused connect's message: `invalid_key` goes under the key field, with
 * the provider's reason; anything else (`invalid_model`,
 * `ai_provider_unavailable`, `ai_busy`) under the form (slice 1b spec §6.2).
 */
export function connectErrors(error: unknown): { key: string | null; other: string | null } {
  if (!error) return { key: null, other: null };
  if (error instanceof ApiError && error.code === "invalid_key") {
    return { key: error.hint, other: null };
  }
  return { key: null, other: errorHint(error) };
}

/** The connect button: Sign in with ChatGPT (or Grok), or Connect (Try again after a failure). */
export function connectLabel(
  provider: AiProvider,
  state: { pending: boolean; retrying: boolean },
): string {
  const signIn = signsIn(provider);
  if (state.pending) return signIn ? "Starting…" : "Checking the key…";
  if (signIn) return `Sign in with ${PROVIDERS[provider].name}`;
  return state.retrying ? "Try again" : "Connect";
}

/** A provider's models from the catalog; none while it loads, or without a provider. */
export function providerModels(
  catalog: Catalog | undefined,
  provider: AiProvider | null,
): CatalogModel[] {
  return (provider && catalog?.[provider]) || [];
}

/**
 * The model the connect form starts on: the workspace's own when it's in the
 * list (a Reconnect keeps it), else the provider's default (a new connection,
 * or a switch).
 */
export function initialModel(
  models: readonly CatalogModel[],
  kept: string | null,
): string | undefined {
  return models.some((m) => m.id === kept) ? (kept as string) : defaultModelOf(models);
}

/**
 * The model a connect sends: the one picked, else `initialModel`; none when
 * the form doesn't ask (a connect beside a working account keeps the
 * default, slice 1c spec §7).
 */
export function formModel(
  ask: boolean,
  picked: string | undefined,
  models: readonly CatalogModel[],
  kept: string | null,
): string | undefined {
  if (!ask) return undefined;
  return picked ?? initialModel(models, kept);
}

/** The workspace's model if `provider` is the one it's on, else null. */
export function keptModel(ai: Ai, provider: AiProvider): string | null {
  return ai.provider === provider ? ai.model : null;
}

/** The provider the form starts on: the fixed one (Reconnect), or a failed connect's. */
export function initialProvider(ai: Ai, fixed: AiProvider | undefined): AiProvider | null {
  return fixed ?? (ai.status === "failed" ? ai.provider : null);
}

/**
 * `POST /api/computer/ai`'s body: ChatGPT and Grok sign in, the others send their
 * key. With `connectionId` (Reconnect), the new credential replaces that
 * account's rather than adding one.
 */
export function connectBody(
  provider: AiProvider,
  model: string | undefined,
  apiKey: string,
  connectionId?: string,
  accountEmail?: string,
  accountId?: string,
) {
  // A Claude subscription's optional account email: its label (slice 1g spec §3.2).
  const email = provider === "claude" ? accountEmail?.trim() : undefined;
  // Workers AI's Cloudflare account (slice 1h spec §3.2).
  const cloudflare = provider === "workers-ai" ? accountId?.trim().toLowerCase() : undefined;
  const body = signsIn(provider)
    ? { provider, model }
    : {
        provider,
        model,
        api_key: apiKey.trim(),
        ...(email ? { account_email: email } : {}),
        ...(cloudflare ? { account_id: cloudflare } : {}),
      };
  return connectionId ? { ...body, connection_id: connectionId } : body;
}

/**
 * The account a connect started (slice 1c): the one `POST /api/computer/ai`
 * answers `connecting`, new or replaced, with when it last connected before.
 */
export type StartedAccount = { id: string; connectedAt: string | null };

/** The account in a connect's `{ ai }` answer, if the server names one. */
export function startedAccount(ai: Ai): StartedAccount | undefined {
  const account = ai.connections?.find((c) => c.status === "connecting");
  return account ? { id: account.id, connectedAt: account.connected_at } : undefined;
}

/** A connect this panel started: its provider, and the account it's for. */
export type StartedConnect = { provider: AiProvider; account?: StartedAccount };

/**
 * Whether the account a connect started has connected since: a replace that
 * failed leaves it connected on its old credential, so it counts only with a
 * newer `connected_at`.
 */
function accountDone(ai: Ai, started: StartedAccount): boolean {
  const account = ai.connections?.find((c) => c.id === started.id);
  return account?.status === "connected" && account.connected_at !== started.connectedAt;
}

/**
 * What the connect panel shows (slice 1b spec §6.2): the form; a sign-in,
 * ChatGPT's or Grok's (its code is there, or this panel started one: a reload mid
 * sign-in has only the code to go by); "Connecting…"; or `done` once its own
 * connect has finished. Since slice 1c a workspace can be connected while
 * the account this panel connects fails, so it follows that account, not
 * the workspace.
 */
export function connectView(
  ai: Ai,
  started: StartedConnect | undefined,
): "form" | "sign-in" | "connecting" | "done" {
  if (ai.status === "connecting") {
    return ai.pending || signsIn(started?.provider) ? "sign-in" : "connecting";
  }
  if (!started) return "form";
  const done = started.account ? accountDone(ai, started.account) : ai.status === "connected";
  return done ? "done" : "form";
}

/**
 * Which provider a sign-in under way is for: the connecting account's, else
 * the connect this panel started, else the panel's own. ChatGPT when none
 * says (a slice 1b sign-in has no account yet).
 */
export function signInProvider(
  ai: Ai,
  started: StartedConnect | undefined,
  fixed?: AiProvider,
): SignInProvider {
  const account = ai.connections?.find((c) => c.status === "connecting");
  for (const provider of [account?.provider, started?.provider, fixed]) {
    if (signsIn(provider)) return provider;
  }
  return "chatgpt";
}

/**
 * Why the connect didn't work, above the form (whose button then says Try
 * again): the account it started failed, with its reason, or the workspace's
 * own connect failed. Null otherwise (a cancelled sign-in, say).
 */
export function connectFailure(ai: Ai, started: StartedConnect | undefined): string | null {
  if (ai.status === "connecting") return null;
  const account = started?.account && ai.connections?.find((c) => c.id === started.account!.id);
  if (account?.status === "failed") return account.error ?? ai.error ?? null;
  if (started?.account && !accountDone(ai, started.account) && ai.error) return ai.error;
  return ai.status === "failed" ? (ai.error ?? null) : null;
}

/** A model change that didn't apply leaves the connection as it was, with why (§4.2). */
export function modelChangeError(ai: Ai): string | null {
  return (ai.status === "connected" && ai.error) || null;
}

/** The rows a connect's stages show as (slice 1b spec §4.3), in order. */
const STAGES: Array<Exclude<ConnectStage, "signing_in">> = ["waking", "saving", "starting"];

export type StageRow = { stage: ConnectStage; label: string; state: StepState };

/**
 * A stage's label: the credential is a key, or a sign-in, which is
 * "Signing in to ChatGPT" (or Grok) until it's approved.
 */
function stageLabel(stage: ConnectStage, provider: AiProvider | null, signedIn: boolean): string {
  if (stage === "waking") return "Waking your computer";
  if (stage === "starting") return "Starting your Chief of Staff";
  if (provider === "claude") return "Saving your token on it";
  if (!signsIn(provider)) return "Saving your key on it";
  return signedIn ? "Saving your sign-in on it" : `Signing in to ${PROVIDERS[provider].name}`;
}

/**
 * The connect's stages: before `ai.stage` done, it running (or failed, when
 * the connect did), the rest pending. The sign-in is the saving row. A
 * connect with no stage yet is at the first; a connected AI has done them all.
 */
export function connectStages(
  ai: Pick<Ai, "status" | "stage">,
  provider: AiProvider | null,
): StageRow[] {
  // `waiting` (slice 1j §5.3) is inside the rollout: the starting row here.
  const stage =
    ai.stage === "signing_in"
      ? "saving"
      : ai.stage === "waiting"
        ? "starting"
        : (ai.stage ?? "waking");
  const at = ai.status === "connected" ? STAGES.length : STAGES.indexOf(stage);
  const signedIn = ai.status === "connected" || (ai.stage !== "signing_in" && at >= 1);
  return STAGES.map((stage, i) => {
    const label = stageLabel(stage, provider, signedIn);
    if (i < at) return { stage, label, state: "done" };
    if (i > at) return { stage, label, state: "pending" };
    return { stage, label, state: ai.status === "failed" ? "failed" : "running" };
  });
}

/**
 * The account a connect is for: the one connecting, else a failed one, else
 * the newest. Null before the server names one (a slice 1b response).
 */
export function connectAccount(ai: Ai): AiConnection | null {
  const all = ai.connections ?? [];
  return (
    all.find((c) => c.status === "connecting") ??
    all.find((c) => c.status === "failed") ??
    all.at(-1) ??
    null
  );
}
